import { ReactElement, useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useDrop, useDrag } from 'react-dnd';

import { Nullable } from '~globals/types/commons';

import {
  RoadmapColumnsFieldSortableItemDragData,
  RoadmapColumnsFieldSortableItemProps,
} from './types';
import { ITEM_TYPES_DND } from './utils';

const RoadmapColumnsFieldSortableItem = ({
  columnId,
  value,
  findColumnIndex,
  onMove,
  onRemove,
  disabled = false,
}: RoadmapColumnsFieldSortableItemProps): ReactElement => {
  const originalIndex = useMemo(
    () => findColumnIndex(columnId),
    [columnId, findColumnIndex],
  );

  const [{ isDragging }, dragConnect, dragPreviewConnect] = useDrag(
    () => ({
      type: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
      item: { columnId, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const {
          columnId: droppedColumnId,
          originalIndex: dropperOriginalIndex,
        } = item;

        if (!monitor.didDrop()) {
          onMove(droppedColumnId, dropperOriginalIndex);
        }
      },
    }),
    [columnId, onMove],
  );

  const [, dropConnect] = useDrop(
    () => ({
      accept: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
      hover(item: RoadmapColumnsFieldSortableItemDragData) {
        const { columnId: draggedColumnId } = item;

        if (draggedColumnId !== columnId) {
          onMove(draggedColumnId, originalIndex);
        }
      },
    }),
    [columnId, onMove, originalIndex],
  );

  const connectRef = useCallback(
    (node: Nullable<HTMLDivElement>) =>
      !disabled ? dragPreviewConnect(dropConnect(node)) : node,
    [disabled, dragPreviewConnect, dropConnect],
  );

  return (
    <Box
      ref={connectRef}
      sx={(theme) => ({
        opacity: isDragging ? 0 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        paddingLeft: 0.5,
        paddingRight: 0.5,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <IconButton
        aria-label="order column"
        size="small"
        disabled={disabled}
        ref={dragConnect}
        sx={{
          '&:not(:disabled)': {
            cursor: 'move',
          },
        }}
        disableFocusRipple
        disableRipple
        disableTouchRipple
      >
        <DragIcon fontSize="inherit" />
      </IconButton>

      <Typography variant="caption" noWrap>
        {value}
      </Typography>

      <Tooltip title="Eliminar columna" arrow placement="top">
        <span>
          <IconButton
            aria-label="Delete column"
            onClick={() => onRemove(originalIndex)}
            size="small"
            disabled={disabled || isDragging}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default RoadmapColumnsFieldSortableItem;
